import { clientGeneratableIdSchema } from "../../shared/id";
import { GALLERY_ARTIFACTS_ORIGIN } from "../../shared/galleryArtifacts";
import { MAX_ZIP_BYTES } from "../../shared/zip";

export function galleryWorkflowArtifactUrl(
  workflowId: string,
  commitId: string
): string {
  if (
    !clientGeneratableIdSchema.safeParse(workflowId).success ||
    !clientGeneratableIdSchema.safeParse(commitId).success
  ) {
    throw new Error("Invalid gallery workflow URL");
  }
  return new URL(
    `/v1/workflows/${workflowId}/${commitId}.zip`,
    GALLERY_ARTIFACTS_ORIGIN
  ).toString();
}

type GalleryArtifactRequest = {
  workflowId: string;
  commitId: string;
  sha256: string;
};

export async function fetchGalleryWorkflowArtifact({
  workflowId,
  commitId,
  sha256,
}: GalleryArtifactRequest): Promise<File> {
  const response = await fetch(
    galleryWorkflowArtifactUrl(workflowId, commitId),
    {
      credentials: "omit",
      redirect: "error",
    }
  );
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? "Gallery workflow not found"
        : "Couldn’t download gallery workflow"
    );
  }

  const contentLengthHeader = response.headers.get("content-length");
  const contentLength =
    contentLengthHeader === null ? NaN : Number(contentLengthHeader);
  if (!Number.isSafeInteger(contentLength) || contentLength < 0) {
    throw new Error("Gallery workflow response has no valid content length");
  }
  if (contentLength > MAX_ZIP_BYTES) {
    throw new Error(
      `Gallery workflow ZIP exceeds ${MAX_ZIP_BYTES / 1024 / 1024} MiB`
    );
  }

  const blob = await response.blob();
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await blob.arrayBuffer()
  );
  const actual = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
  if (actual !== sha256) {
    throw new Error("Gallery workflow failed integrity verification");
  }

  return new File([blob], `${commitId}.zip`, {
    type: "application/zip",
  });
}
